import React from "react";
import { Control, Controller } from "react-hook-form";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FormattedMessage } from "react-intl";
import { HiOutlineCalendar, HiOutlineX } from "react-icons/hi";
import { IconWrapper } from "../utils/IconWrapper";
import { IoIosHelpCircleOutline } from "react-icons/io";

interface FormDateProps {
  id: string;
  value?: Date | undefined | null;
  question?: string;
  containerStyle?: string;
  divider?: string;
  repeatable?: boolean;
  inputStyle?: string;
  divStyle?: string;
  labelStyle?: string;
  dividerHeaderStyle?: string;
  register?: any;
  errors?: Record<string, Object>;
  control: Control<Record<string, any>>;
  link?: string | boolean;
  cursiveLabel?: string;
  repeatableHeader?: string | boolean;
  repeatableKey?: { key: string; last: boolean; amount: number };
  required?: boolean;
  overlayId?: string;
  reset: (event: any, id: string) => void;
  overlayToggle?: (id: string) => void;
  changeAmount?: (key: string, direction: number) => void;
  handleInputChange: (
    event: any,
    date: Date | [Date, Date],
    type?: string
  ) => void;
}

interface InputProps {
  value: Date | undefined | null;
  onClick: any;
  reset: (event: any, id: string) => void;
}

export const FormDate: React.FC<FormDateProps> = ({
  id,
  handleInputChange,
  question,
  value,
  containerStyle,
  divider,
  repeatable,
  link,
  repeatableHeader,
  changeAmount,
  reset,
  overlayId,
  inputStyle,
  labelStyle,
  divStyle,
  dividerHeaderStyle,
  register,
  errors,
  control,
  cursiveLabel,
  repeatableKey,
  required,
  overlayToggle,
}) => {
  const styles = inputStyle
    ? inputStyle
    : "font-semibold text-brand-dark_blue focus:outline-none focus:bg-blue-100 focus:shadow-inner border border-gray-300 rounded-lg py-2 px-4 m-4 appearance-none leading-normal";

  const divStyles = divStyle ? divStyle : "flex flex-col mb-4";

  const labelStyles = labelStyle
    ? labelStyle
    : "m-2 uppercase font-bold text-lg text-brand-dark_blue";

  const linkStyles =
    "hover:text-brand-dark_blue text-brand-blue mb-2 uppercase font-bold text-lg";

  const participantId = repeatable ? id.split("_")[1] : ""; //expected id is 'BLABLABLA_2' => 2

  const dividerHeaderStyles = dividerHeaderStyle
    ? dividerHeaderStyle
    : "m-2 uppercase font-bold text-lg text-brand-blue";

  const dividerStyles = divider ? divider : "";
  const containerStyles = containerStyle ? containerStyle : "";

  const requiredAsterisk = required ? (
    <span className='font-normal text-red-500'>*</span>
  ) : null;

  const ref = React.createRef<HTMLInputElement>();

  const mouseHoverTrigger = (e: any) => {
    overlayToggle(overlayId);
  };

  const helpIcon = overlayId ? (
    <IconWrapper styles={"self-center"}>
      <IoIosHelpCircleOutline
        onMouseEnter={(e) => mouseHoverTrigger(e)}
        onMouseLeave={(e) => mouseHoverTrigger(e)}
        size={30}
      ></IoIosHelpCircleOutline>
    </IconWrapper>
  ) : null;

  const labelTag =
    typeof link === "string" ? (
      <span className={`${linkStyles}`}>
        <a href={link} target='_blank' rel='noreferrer'>
          {question}
          {requiredAsterisk}
        </a>
      </span>
    ) : question && overlayId ? (
      <div className='flex relative'>
        <span className={`${labelStyles}`}>
          {question}
          {requiredAsterisk}
        </span>
        {helpIcon}
      </div>
    ) : question ? (
      <span className={`${labelStyles}`}>
        {question}
        {requiredAsterisk}
      </span>
    ) : null;

  const errorDiv = errors ? (
    errors[id] ? (
      <p className='text-red-400 ml-4'>
        {" "}
        <FormattedMessage id={errors[id]["message"]} />
      </p>
    ) : null
  ) : null;

  const additionalLabel = cursiveLabel ? (
    <p className='text-brand-dark_blue italic ml-4'>{cursiveLabel}</p>
  ) : null;

  const buttonStyle =
    "bg-blue-400 hover:bg-brand-blue text-brand-white py-2 px-4 rounded m-5 w-auto";
  const hideDecreaseRepeatableStyle = repeatableKey
    ? Number(repeatableKey.amount) === 1
      ? "invisible focus:outline-none outline-none"
      : "visible"
    : null;
  const lastOfRepeatGroup = repeatableKey
    ? repeatableKey.last
      ? true
      : false
    : false;

  const dateTimeZoneFixed =
    value !== undefined && value !== null
      ? new Date(value.getTime() + value.getTimezoneOffset() * 60000)
      : value;

  function timeZoneFormatting(date: Date | [Date, Date]) {
    if (date instanceof Array) {
      return date;
    }
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  }

  const repeatableButtons = lastOfRepeatGroup ? (
    <div className='flex flex-row justify-between'>
      <button
        onClick={() => changeAmount(repeatableKey.key, 1)}
        type='button'
        className={`${buttonStyle}`}
      >
        <FormattedMessage id='Form.Repeat_add' />
      </button>
      <button
        onClick={() => changeAmount(repeatableKey.key, -1)}
        type='button'
        className={`${buttonStyle} ${hideDecreaseRepeatableStyle}`}
      >
        <FormattedMessage id='Form.Repeat_remove' />
      </button>
    </div>
  ) : null;

  const InputWithIcons = React.forwardRef(
    (
      { value, onClick, reset }: InputProps,
      ref: React.Ref<HTMLInputElement>
    ) => (
      <div
        className='flex h-10 w-64 bg-white border border-gray-300 rounded-lg relative'
        ref={ref}
        onClick={onClick}
      >
        <IconWrapper styles='ml-2 mt-1'>
          <HiOutlineCalendar size={30} />
        </IconWrapper>
        <span className='mt-2 ml-4 font-semibold'>{value}</span>
        {value ? (
          <IconWrapper styles='cursor-pointer ml-auto mt-2 hover:text-red-400'>
            <HiOutlineX onClick={(e) => reset(e, id)} size={24} />
          </IconWrapper>
        ) : null}
      </div>
    )
  );

  return (
    <div className={`${divStyles} ${containerStyles} ${dividerStyles}`}>
      {divider && repeatable ? (
        <span className={`${dividerHeaderStyles}`}>
          {repeatableHeader} {participantId}
        </span>
      ) : null}
      {labelTag}
      {additionalLabel}
      <Controller
        name={id}
        control={control}
        rules={register}
        defaultValue={new Date()}
        render={({ name, onChange }) => (
          <DatePicker
            selected={dateTimeZoneFixed}
            showTimeSelect
            dateFormat='Pp'
            className={styles}
            onFocus={(e) => e.target.blur()}
            name={name}
            onChange={(value, e) => {
              onChange(value);
              handleInputChange(e, timeZoneFormatting(value), name);
            }}
            customInput={
              <InputWithIcons
                value={value}
                onClick={onChange}
                reset={reset}
                ref={ref}
              />
            }
          />
        )}
      />
      {errorDiv}
      {repeatableButtons}
    </div>
  );
};

export default FormDate;
